import React, { useEffect, useState } from "react";
import { Button, Grid } from "@mui/material";
import { useIntl } from "react-intl";

import styles from "./index.module.css";

import GetImages from "@/components/GetImages";
import ShareDialog from "@/views/Patient/VideoDetails/ShareDialog";

import {
  favouriteVideoApi,
  likeVideoApi,
} from "@/utils/apis/patient/healthHub";
import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";
import { useHealthHubGuestHandler } from "@/utils/hooks/useHealthHubGuestHandler";

type VideoActionsProps = {
  isLiked?: boolean;
  isFavourited?: boolean;
  videoId: string;
  likesCount?: number;
};

const VideoActions: React.FC<VideoActionsProps> = ({
  isLiked,
  isFavourited,
  videoId,
  likesCount,
}) => {
  const intl = useIntl();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isVideoLiked, setIsVideoLiked] = useState(isLiked);
  const [isVideoFavourited, setIsVideoFavourited] = useState(isFavourited);
  const [likes, setLikes] = useState(likesCount);

  const { handleHealthHubGuestAccess } = useHealthHubGuestHandler();

  useEffect(() => {
    setIsVideoLiked(isLiked);
    setIsVideoFavourited(isFavourited);
  }, [videoId]);

  const handleLike = async () => {
    const hasAccess = handleHealthHubGuestAccess(
      `/health-hub/video?videoId=${videoId}`
    );
    if (!hasAccess) return;

    const response = await likeVideoApi(videoId);
    if (response.statusCode === IAPIStatusCode.SUCCESS) {
      setIsVideoLiked(true);
      setLikes(likes! + 1);
    }
  };

  const handleUnLike = async () => {
    const hasAccess = handleHealthHubGuestAccess(
      `/health-hub/video?videoId=${videoId}`
    );
    if (!hasAccess) return;

    const response = await likeVideoApi(videoId, true);
    if (response.statusCode === IAPIStatusCode.SUCCESS) {
      setIsVideoLiked(false);
      setLikes(likes! - 1);
    }
  };

  const handleFavourite = async () => {
    const hasAccess = handleHealthHubGuestAccess(
      `/health-hub/video?videoId=${videoId}`
    );
    if (!hasAccess) return;

    const response = await favouriteVideoApi(videoId);
    if (response.statusCode === IAPIStatusCode.SUCCESS) {
      setIsVideoFavourited(true);
    }
  };

  const handleUnfavourite = async () => {
    const hasAccess = handleHealthHubGuestAccess(
      `/health-hub/video?videoId=${videoId}`
    );
    if (!hasAccess) return;

    const response = await favouriteVideoApi(videoId, true);
    if (response.statusCode === IAPIStatusCode.SUCCESS) {
      setIsVideoFavourited(false);
    }
  };

  const handleShare = () => {
    setIsDialogOpen(true);
  };

  const shareUrl = `${window.location.host}/health-hub/video`;

  return (
    <Grid className={styles.actionsContainer}>
      <Button
        variant="contained"
        className={`${styles.actionButton} ${isVideoLiked ? styles.liked : ""}`}
        onClick={isVideoLiked ? handleUnLike : handleLike}
      >
        <GetImages
          name="LikeVideoDesktopIcon"
          fill={isVideoLiked ? "white" : "#C6C6C6"}
          height="20"
          width="20"
        />{" "}
        <span className={styles.actionText}>
          <span>
            {likes ??
              intl.formatMessage({ id: "like", defaultMessage: "Like" })}
          </span>
        </span>
      </Button>
      <Button
        variant="contained"
        className={`${styles.actionButton} ${
          isVideoFavourited ? styles.favourited : ""
        }`}
        onClick={isVideoFavourited ? handleUnfavourite : handleFavourite}
      >
        <GetImages
          fill={isVideoFavourited ? "white" : "#C6C6C6"}
          name="FavouriteVideoDesktopIcon"
          height="20"
          width="20"
        />{" "}
        <span className={styles.actionText}>
          {intl.formatMessage({ id: "favourite", defaultMessage: "Favourite" })}
        </span>
      </Button>
      <Button
        variant="contained"
        className={styles.actionButton}
        onClick={handleShare}
      >
        <GetImages
          name="ShareVideoDesktopIcon"
          fill="#393939"
          height="20"
          width="20"
        />{" "}
        <span className={styles.actionText}>
          {intl.formatMessage({ id: "share", defaultMessage: "Share" })}
        </span>
      </Button>

      <ShareDialog
        url={`${shareUrl}?id=${videoId}`}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </Grid>
  );
};

export default VideoActions;
